/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import InputBox from "../../../components/InputBox";
import { Button } from "src/business-types/grocery/components/Button";
import iconMap from "../../../../../assets/icons/Icons";
import BarcodeComponent from "../../../../../shared/utils/generateBarcode/BarcodeComponent";
import { postProduct } from "../../../../../shared/services/services";
import { CategoryDetails } from "../../../../../shared/services/interface";
import { closeLoading, notifyError, notifyLoading, notifySuccess, notifyWarning } from "../../../../../assets/toastService";
import DropdownSelect from "../../../components/DropdownSelect";
import BarcodeScanner from "../../../../../shared/utils/scanningBarcode/BarcodeScanner";
import PopupModules from "src/features/auth/components/PopupModules";
import { ThemeBasedColorHeader, ThemeBasedColorMenubar } from "src/shared/modules/profile/theme/themeCheacker";
import { errorHandling } from "src/shared/services/errorHandling";
import useUOM from "src/shared/hooks/useUOM";
import MultiSelect from "src/business-types/grocery/components/MultiSelect";

type Props = {
  close: (id: string) => void;
  category: CategoryDetails[];
  loading: (state: boolean) => void;
  addmul?: boolean;
  addProduct: boolean;
};

type FormData = {
  ProductName: string;
  Amount: string;
  Type: string;
  ArabicName: string;
  own_product: boolean;
  category_id: string;
  categoryName: string;
  staff_list: string[];
  image: File | null;
  ImagePreview: string;
  barcodeId: string;
  uom: uomFormate[];
  product_id: string | number;
  Stock: number;
  amountMeasurement: string;
  purchasePrice: number;
  sellingPrice: number;
  discount: number;
  discountType: boolean;
};

type uomFormate = {
  _id: string;
  uomName: string;
};

const AddProducts: React.FC<Props> = ({ close, category, loading, addmul, addProduct }) => {
  const [productType, setProductType] = useState<"Inward" | "Service">("Inward");
  const [formData, setFormData] = useState<FormData>({
    ProductName: "",
    Amount: "",
    Type: "",
    ArabicName: "",
    own_product: false,
    category_id: "",
    categoryName: "",
    staff_list: [],
    image: null,
    ImagePreview: "",
    barcodeId: "",
    uom: [],
    product_id: "",
    Stock: 0,
    amountMeasurement: "",
    purchasePrice: 0.0,
    sellingPrice: 0.0,
    discount: 0.0,
    discountType: false,
  });

  const { uom } = useUOM();
  const [barcodeMode, setBarcodeMode] = useState(false);
  const [scanMode, setScanMode] = useState(false);
  const [multiAdd, setMultiAdd] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [submit, setSubmit] = useState(false);
  const BarcodeIcon = iconMap["BarcodeReaderIcon"];

  // 🧠 Auto-fill category name when selected
  useEffect(() => {
    const selectedCategory = category.find((i) => i._id.$oid === formData.category_id);
    if (selectedCategory) {
      setFormData((prev) => ({ ...prev, categoryName: selectedCategory.CategoryName }));
    }
  }, [formData.category_id, category]);

  // 🖼️ Handle Image Compression (max 100KB)
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const img = new Image();
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === "string") img.src = reader.result;
    };

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const maxWidth = 150;
      const scale = maxWidth / img.width;
      canvas.width = maxWidth;
      canvas.height = img.height * scale;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        if (blob && (blob.size <= 160 * 1024 || blob.size <= 160000)) {
          const compressed = new File([blob], "image.png", { type: "image/png" });
          setFormData((prev) => ({
            ...prev,
            image: compressed,
            ImagePreview: URL.createObjectURL(compressed),
          }));
        } else {
          notifyError("Image must be under 100KB");
        }
      }, "image/png");
    };

    reader.readAsDataURL(file);
  };

  // 🔍 Barcode Scanning
  const handleBarcodeDetected = (code: string) => {
    setFormData((prev) => ({ ...prev, barcodeId: code }));
    setScanMode(false);
    setBarcodeMode(true);
  };

  // 🚀 Submit Product
  const handleSubmit = async () => {
    setSubmit(true);
    notifyLoading("Adding product...");
    const discount = formData.discount + (formData.discountType === true ? "%" : "");
    try {
      // Create FormData instance
      const payload = new FormData();
      // Append normal string/number fields
      payload.append("ProductName", formData.ProductName);
      payload.append("Amount", formData.Amount);
      payload.append("ArabicName", formData.ArabicName);
      payload.append("Type", formData.Type);
      payload.append("own_product", String(productType === "Inward"));
      payload.append("category_id", formData.category_id);
      payload.append("categoryName", formData.categoryName);
      payload.append("product_id", String(formData.product_id));
      payload.append("Stock", String(formData.Stock));
      payload.append("barcodeId", formData.barcodeId);
      payload.append("uom", JSON.stringify(formData.uom));
      payload.append("amountMeasurement", formData.amountMeasurement);
      payload.append("purchasePrice", String(formData.purchasePrice));
      payload.append("sellingPrice", String(formData.sellingPrice));
      payload.append("discount", discount ?? 0);

      if (formData.image) {
        payload.append("image", formData.image);
      }
      const res = await postProduct(payload);
      notifySuccess("Product added successfully");
      if (!multiAdd) close(res.data._id);
      resetForm();
    } catch (err) {
      errorHandling(err);
    } finally {
      closeLoading();
      setSubmit(false);
    }
  };

  // ♻️ Reset form
  const resetForm = () =>
    setFormData({
      ProductName: "",
      Amount: "",
      Type: "",
      ArabicName: "",
      own_product: false,
      category_id: "",
      categoryName: "",
      staff_list: [],
      image: null,
      ImagePreview: "",
      barcodeId: "",
      uom: [],
      product_id: "",
      Stock: 0,
      amountMeasurement: "",
      purchasePrice: 0.0,
      sellingPrice: 0.0,
      discount: 0.0,
      discountType: false,
    });

  // ✅ Validation
  const canSubmit = formData.ProductName && formData.category_id && !submit && formData.amountMeasurement && formData.uom.length !== 0;

  const renderInput = (
    label: string,
    name: keyof FormData,
    type = "text",
    mandatory: boolean = false,
    onlyNumber: boolean = false,
    disabled: boolean = false,
  ) => (
    <div className='mb-3' id={label}>
      <label htmlFor={name} className='flex'>
        <p className='text-sm font-medium mb-1'>{label}</p>
        {mandatory && <p className='text-red-600'>*</p>}
      </label>
      <InputBox
        id={name}
        type={type}
        value={formData[name] as string}
        onChange={(e) => {
          let value = e.target.value;

          if (onlyNumber === true) {
            // ✅ Allow only numbers with max 2 decimal places
            const regex = /^\d*\.?\d{0,2}$/;

            if (!regex.test(value)) return;
          }

          setFormData({ ...formData, [name]: value });
        }}
        onKeyDown={(e) => {
          if (onlyNumber === true && ["-", "e", "E"].includes(e.key)) e.preventDefault();
        }}
        maxLength={40}
        min={onlyNumber === true ? 0.01 : undefined}
        onlyNumber={onlyNumber}
        disabled={disabled}
      />
    </div>
  );

  useEffect(() => {
    if (formData.uom.length === 0) {
      setFormData((prev) => ({ ...prev, Amount: "", amountMesurment: "" }));
    }
  }, [formData.uom]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const isPercentage = formData.discountType === true;
      const discountAmount = Number(formData.discount);
      const sellingPrice = Number(formData.sellingPrice);
      const purchasePrice = Number(formData.purchasePrice);

      // % discount cannot exceed 99
      if (isPercentage && discountAmount > 99) {
        notifyWarning("Percentage discount cannot be more than 99%");
        setFormData((prev) => ({ ...prev, discount: 0.0 }));
        return;
      }

      if (sellingPrice === 0 && purchasePrice === 0) {
        return;
      }

      // Flat discount cannot be >= selling price
      if (!isPercentage && discountAmount >= sellingPrice) {
        notifyWarning("Discount amount must be less than the selling price");
        return;
      }

      // Selling price must be > purchase price
      if (sellingPrice <= purchasePrice) {
        notifyWarning("Selling price must be greater than the purchase price");
        return;
      }

      // Discounted price must be > purchase price
      if (!isPercentage && purchasePrice >= sellingPrice - discountAmount) {
        notifyWarning("Discount price must be greater than the purchase price");
        return;
      }
    }, 500); // 👈 delay time in milliseconds

    return () => clearTimeout(timer); // cleanup
  }, [formData.discount, formData.discountType, formData.sellingPrice, formData.purchasePrice]);

  const sellingPrice = Number(formData.sellingPrice) || 0;
  const discount = Number(formData.discount) || 0;
  const purchasePrice = Number(formData.purchasePrice);

  const discountPrice = formData.discountType
    ? (sellingPrice * discount) / 100 // Percentage discount
    : discount;

  const finalPrice = sellingPrice - discountPrice;
  return (
    <PopupModules isOpen={addProduct} onClose={() => close("")} title='Add New Product'>
      <div className='w-full max-w-md space-y-3'>
        {/* Toggle Product Type */}
        <div className='flex gap-2 mb-3'>
          {["Inward", "Service"].map((type) => (
            <button
              key={type}
              onClick={() => {
                setProductType(type as "Inward" | "Service");
                setFormData((prev) => ({ ...prev, own_product: type === "Inward" }));
              }}
              className={`flex-1 py-1.5 rounded-md text-sm font-medium ${productType === type ? ThemeBasedColorHeader() : ThemeBasedColorMenubar()}`}
            >
              {type} Products
            </button>
          ))}
        </div>

        {/* Product Form */}
        <div className={`border border-gray-300 rounded-xl p-3 bg-gray-50 shadow-inner space-y-2`}>
          <div className='flex gap-2'>
            <div className='w-3/5'>
              {renderInput("Product Name", "ProductName", "text", true)}
              {renderInput("Second Language Name", "ArabicName")}
              <DropdownSelect
                title='Category Type'
                name='category_id'
                value={formData.category_id}
                options={category.map((c) => ({ _id: c._id.$oid, CategoryName: c.CategoryName, otherName: c.categorySecondName }))}
                onChange={(e) => setFormData({ ...formData, category_id: e })}
                mandatory={true}
              />
            </div>

            <div className='w-2/5 flex flex-col items-center'>
              <div
                onClick={() => fileRef.current?.click()}
                className='cursor-pointer border border-gray-300 w-[139px] h-[139px] rounded-lg overflow-hidden flex items-center justify-center bg-white'
              >
                {formData.ImagePreview ? (
                  <img src={formData.ImagePreview} alt='Preview' className='w-full h-full object-cover' />
                ) : (
                  <span className='text-xs text-gray-400 text-center px-2'>Upload Image (under 100KB)</span>
                )}
                <input ref={fileRef} type='file' accept='image/*' onChange={handleImageChange} className='hidden' />
              </div>
              {renderInput("Product ID", "product_id")}
            </div>
          </div>

          {productType !== "Inward" && renderInput("Amount", "Amount", "text", true)}
          {productType === "Inward" && (
            <>
              <div className='flex w-full'>
                <MultiSelect
                  title='Unit Of Measurement'
                  name='uom'
                  value={formData.uom.map((u) => u.uomName)}
                  options={uom.map((u) => ({ _id: u.uom_name, CategoryName: u.uom_name }))}
                  onChange={(selected) =>
                    setFormData({ ...formData, uom: selected.map((s) => ({ _id: uom.filter((i) => i.uom_name === s)[0]?.id || s, uomName: s })) })
                  }
                  mandatory={true}
                />
              </div>
              <div className='flex gap-2 w-full justify-between items-center '>
                <div className='w-1/2'>
                  <DropdownSelect
                    title='Amount of UOM'
                    name='uomBaseUnit'
                    value={formData.amountMeasurement}
                    options={formData.uom.map((c) => ({
                      _id: c._id,
                      CategoryName: uom.find((u) => u.uom_name === c.uomName)?.uom_name || c.uomName,
                    }))}
                    onChange={(e) => {
                      setFormData({ ...formData, amountMeasurement: e });
                      console.log(e);
                    }}
                    mandatory={true}
                    defultSelect={true}
                  />
                </div>
                {renderInput("Purchase Price", "purchasePrice", "text", false, true, formData.uom.length === 0)}
              </div>
              <div className='flex gap-2'>
                {renderInput("Selling Price", "sellingPrice", "text", false, true, formData.uom.length === 0)}
                <div className='flex flex-col gap-1 items-end w-1/2'>
                  <div className='flex flex-col justify-end items-end -mb-7 w-full gap-1 mr-4'>
                    <div className='flex gap-2 justify-center items-center'>
                      <p className='select-none'> in %</p>
                      <input
                        type='checkbox'
                        checked={formData.discountType}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            discountType: e.target.checked,
                          }))
                        }
                        className='cursor-pointer scale-125'
                      />
                    </div>
                  </div>
                  <div className='w-full'>{renderInput("Discount", "discount", "text", false, true)}</div>
                </div>
              </div>
              <div className='w-full flex justify-between items-center gap-2'>
                <div className={`flex w-1/2`}>
                  {" "}
                  <p>Discount Value :</p>
                  <p> {discountPrice.toFixed(2)}</p>
                </div>
                <div
                  className={`flex w-1/2 ${finalPrice < purchasePrice ? "text-red-500" : purchasePrice === finalPrice ? "text-orange-400" : "text-green-600"
                    }`}
                >
                  {" "}
                  <p>Final Price :</p>
                  <p> {finalPrice.toFixed(2)}</p>
                </div>
              </div>

              {purchasePrice > sellingPrice && (
                <p className='text-red-600 text-sm font-medium flex justify-center items-center'>
                  ⚠️ Are you sure the selling price <br />
                  is less than the purchase price?
                </p>
              )}

              <div className='text-center w-full'>
                <div
                  onClick={() => setScanMode((prev) => !prev)}
                  className='flex justify-center items-center gap-2 bg-gray-200 px-3 py-1 rounded-md cursor-pointer'
                >
                  <BarcodeIcon /> <span>Add or Create Barcode</span>
                </div>

                {barcodeMode && (
                  <div className='my-2 flex justify-center'>
                    <BarcodeComponent value={formData.barcodeId} width={1.5} height={20} />
                  </div>
                )}

                {scanMode && <BarcodeScanner onDetected={(code: string) => handleBarcodeDetected(code)} />}
              </div>
            </>
          )}
        </div>

        {/* Multi Add */}
        {!addmul && (
          <div className='flex items-center gap-2 mt-2'>
            <input type='checkbox' checked={multiAdd} onChange={() => setMultiAdd(!multiAdd)} className='w-5 h-5' />
            <p className='text-sm'>Add Multiple Products</p>
          </div>
        )}

        {/* Buttons */}
        <div className='flex justify-center gap-3 mt-3'>
          <Button label='Cancel' variant='ThemeCancel' className='border' onClick={() => close("")} disabled={submit} />
          <Button label={submit ? "Loading ...." : "Confirm"} variant='ThemeBase' onClick={handleSubmit} disabled={!canSubmit} />
        </div>
      </div>
    </PopupModules>
  );
};

export default AddProducts;
